import { FundsTransactionsRequestResponse } from "@/types/FirebaseFunctionsResponses";
import { Functions, httpsCallable } from "firebase/functions";

const TWO_WEEKS_IN_MS = 14 * 24 * 60 * 60 * 1000;

// Platform-agnostic storage interface
interface StorageProvider {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

// Default web storage implementation
const defaultStorage: StorageProvider = {
  getItem: (key: string) => localStorage.getItem(key),
  setItem: (key: string, value: string) => localStorage.setItem(key, value)
};

export class FundsApiService {
  private functions: Functions;
  private storage: StorageProvider;

  constructor(functions: Functions, storage: StorageProvider = defaultStorage) {
    this.functions = functions;
    this.storage = storage;
  }

  private loadCache(accountId: string) {
    const cachedData = this.storage.getItem(`cachedFundsTransactions_${accountId}`);
    const cachedTime = this.storage.getItem(`lastFetchTime_${accountId}`);
    return {
      cachedFundsTransactions: cachedData ? JSON.parse(cachedData) : null,
      lastFetchTime: cachedTime ? parseInt(cachedTime, 10) : 0,
    };
  }

  private saveCache(accountId: string, data: any) {
    this.storage.setItem(`cachedFundsTransactions_${accountId}`, JSON.stringify(data));
    this.storage.setItem(`lastFetchTime_${accountId}`, Date.now().toString());
  }

  async getFundsTransactions(accountId: string, ignoreCache: boolean = false): Promise<FundsTransactionsRequestResponse | undefined> {
    try {
      const { cachedFundsTransactions, lastFetchTime } = this.loadCache(accountId);

      const currentTime = Date.now();
      if (!ignoreCache && cachedFundsTransactions && (currentTime - lastFetchTime) < TWO_WEEKS_IN_MS) {
          return cachedFundsTransactions;
      }

      const getFundsData = httpsCallable(this.functions, 'getFundsTransactions');
      const result = await getFundsData({accountId});

      console.log("FundsApi.getFundsTransactions response:", result);

      const response: FundsTransactionsRequestResponse = result.data as FundsTransactionsRequestResponse;

      console.log("FundsApi.getFundsTransactions response:", response);

      // Update the cache
      this.saveCache(accountId, response);

      return response;
    } catch(e) {
      console.error("FundsApi.getFundsTransactions error:", e);
      return undefined;
    }
  }

  async addTransaction(accountId: string, type: 'deposit' | 'withdrawal', amount: number, date: Date) {
    try {
      const addFundsTransaction = httpsCallable(this.functions, 'addFundsTransaction');
      const result = await addFundsTransaction({accountId, type, amount, date});

      console.log("FundsApi.addTransaction response:", result);
      return result;
    } catch(e) {
      console.error("FundsApi.addTransaction error:", e);
      return undefined;
    }
  }
}

// Backwards compatibility - keeping the old object interface as a reference to the class methods
// This allows existing code that uses FundsApi.method() to continue working
export const FundsApi = {
  getFundsTransactions: async (accountId: string, ignoreCache: boolean = false): Promise<FundsTransactionsRequestResponse | undefined> => {
    // This will be replaced once we initialize the ApiFactory properly
    throw new Error("FundsApi not initialized - use ApiFactoryInstance.getfundsApiService() instead");
  },
  addTransaction: async (accountId: string, type: 'deposit' | 'withdrawal', amount: number, date: Date) => {
    // This will be replaced once we initialize the ApiFactory properly
    throw new Error("FundsApi not initialized - use ApiFactoryInstance.getfundsApiService() instead");
  }
};